"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import BookingSuccessCard from "@/components/common/cards/BookingSuccessCard";
import { useCalEmbed } from "@/hooks/scheduling/useCalEmbed";
import { resolveViewerZone } from "@/lib/utils/cal";
import { scrollToSection } from "@/lib/utils/scroll";
import { BOOKING_SECTION_ID } from "@/lib/constant";
interface CalBookingContextValue {
  /** False when NEXT_PUBLIC_CAL_LINK is missing — callers fall back to /contact. */
  enabled: boolean;
  /** The cal.com event link the inline embed renders. */
  calLink?: string;
  /** Namespace the embed and the themed `cal("ui")` call share. */
  namespace: string;
  /** Set while rescheduling, so the inline booker opens in reschedule mode. */
  rescheduleUid: string | null;
  /**
   * Remount key for the embed. It changes the instant Cal reports a booking,
   * which resets the iframe back to a fresh booker — that is what keeps Cal's
   * own "this meeting is scheduled" screen from ever being seen behind our card.
   */
  embedKey: string;
  /** Takes the visitor to the inline booker, from any page. */
  goToBooking: () => void;
  /**
   * Asks for Cal's embed SDK to be loaded. Nothing Cal-related is fetched
   * until this is called, so whatever is about to need the booker calls it —
   * the embed when it scrolls into range, or any "Book Now" action.
   */
  requestCal: () => void;
  /** True once `cal("ui")` has run; the embed must not mount before this. */
  calReady: boolean;
}

// A no-op default rather than a throw: every consumer already handles
// `enabled: false` by linking to /contact, so a component rendered outside the
// provider degrades to the contact page instead of crashing the page.
const CalBookingContext = createContext<CalBookingContextValue>({
  enabled: false,
  namespace: "",
  rescheduleUid: null,
  embedKey: "new",
  goToBooking: () => {},
  requestCal: () => {},
  calReady: false
});

/** The one way to reach the booker from anywhere — header, nav, floating bar. */
export const useCalBooking = () => {
  return useContext(CalBookingContext);
};

// The visitor's timezone is a client-only read. Modelled as an external store
// rather than state-from-an-effect so it is resolved during render on the
// client and simply reads null on the server, with no cascading re-render.
// It never changes mid-session, so there is nothing to subscribe to.
const subscribeToZone = () => () => {};
const getServerZone = () => null;

// Cal's embed API is a single global: every `useCalEmbed` call registers its own
// `bookingSuccessful` listeners on it, so mounting the hook in three places
// meant three confirmation cards for one booking. It lives here once, at the
// root, and the booker itself is the inline <Cal /> embed inside the page.
const CalBookingProvider = t0 => {
  const $ = _c(29);
  const {
    children
  } = t0;
  const [calRequested, setCalRequested] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setCalRequested(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const requestCal = t1;
  const {
    enabled,
    calLink,
    namespace,
    booking,
    dismissBooking,
    ready: calReady
  } = useCalEmbed("30 min intro with Octarnal", "Octarnal", calRequested);
  const [rescheduleUid, setRescheduleUid] = useState(null);
  const router = useRouter();
  const viewerZone = useSyncExternalStore(subscribeToZone, resolveViewerZone, getServerZone);
  let t2;
  if ($[1] !== router) {
    t2 = () => {
      requestCal();
      if (scrollToSection(BOOKING_SECTION_ID)) {
        return;
      }
      router.push(`/#${BOOKING_SECTION_ID}`);
    };
    $[1] = router;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const goToBooking = t2;
  let t3;
  if ($[3] !== dismissBooking || $[4] !== goToBooking) {
    t3 = uid => {
      setRescheduleUid(uid);
      dismissBooking();
      goToBooking();
    };
    $[3] = dismissBooking;
    $[4] = goToBooking;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleReschedule = t3;
  let t4;
  if ($[6] !== dismissBooking) {
    t4 = () => {
      setRescheduleUid(null);
      dismissBooking();
    };
    $[6] = dismissBooking;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleDismiss = t4;
  let t5;
  if ($[8] !== goToBooking || $[9] !== handleDismiss) {
    t5 = () => {
      handleDismiss();
      goToBooking();
    };
    $[8] = goToBooking;
    $[9] = handleDismiss;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleBookAgain = t5;
  booking?.uid;
  const t6 = rescheduleUid ?? booking?.uid ?? "new";
  let t7;
  if ($[11] !== calLink || $[12] !== calReady || $[13] !== enabled || $[14] !== goToBooking || $[15] !== namespace || $[16] !== rescheduleUid || $[17] !== t6) {
    t7 = {
      enabled,
      calLink,
      namespace,
      rescheduleUid,
      embedKey: t6,
      goToBooking,
      requestCal,
      calReady
    };
    $[11] = calLink;
    $[12] = calReady;
    $[13] = enabled;
    $[14] = goToBooking;
    $[15] = namespace;
    $[16] = rescheduleUid;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const value = t7;
  let t8;
  if ($[19] !== booking || $[20] !== handleBookAgain || $[21] !== handleDismiss || $[22] !== handleReschedule || $[23] !== viewerZone) {
    t8 = <BookingSuccessCard booking={booking} viewerZone={viewerZone} onDismiss={handleDismiss} onReschedule={handleReschedule} onBookAgain={handleBookAgain} />;
    $[19] = booking;
    $[20] = handleBookAgain;
    $[21] = handleDismiss;
    $[22] = handleReschedule;
    $[23] = viewerZone;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== children || $[26] !== t8 || $[27] !== value) {
    t9 = <CalBookingContext.Provider value={value}>{children}{t8}</CalBookingContext.Provider>;
    $[25] = children;
    $[26] = t8;
    $[27] = value;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
export default CalBookingProvider;